export type ExposureTradeSelection = {
  ticker: string;
  portfolioId: string;
  portfolioReportId: string;
  portfolioName: string;
  subAccountId: string;
  financialProductId: string;
  currency: string;
  valueCents: number;
  quantity: number;
  expectedPercentage: number;
  expectedValueCents: number;
  cashAvailableForTradeCents?: number;
  modelPortfolioType?: string;
  objectId?: string;
};

export type ExposureTradeDirection = 'SELL' | 'BUY' | 'REBALANCE';

export type ExposureTradeSide = 'BUY' | 'SELL';

export type ActionableExposureTrade = {
  portfolioId: string;
  source: 'GOAL' | 'SUB_ACCOUNT';
  sourceId: string;
  subAccountId: string;
  financialProductId: string;
  portfolioReportId: string;
  type: ExposureTradeSide;
  moneyAllocatedCents: number;
  currency: string;
  cashAvailableForTradeCents: number;
};

export type ExposureTradeGroup = {
  source: 'GOAL' | 'SUB_ACCOUNT';
  sourceId: string;
  trades: ActionableExposureTrade[];
};

export type BuildExposureTradesResult =
  | { status: 'no_actionable'; skippedGoalCount: number }
  | {
      status: 'ready';
      trades: ActionableExposureTrade[];
      groups: ExposureTradeGroup[];
      skippedGoalCount: number;
    };

// Minimum diff ($1) before a rebalance trade is generated — avoids noise from rounding.
const MIN_REBALANCE_DIFF_CENTS = 100;

function resolveSource(sel: ExposureTradeSelection): {
  source: 'GOAL' | 'SUB_ACCOUNT';
  sourceId: string;
} | null {
  const isGoal = sel.modelPortfolioType === 'GOAL';
  if (isGoal) {
    if (!sel.objectId) return null;
    return { source: 'GOAL', sourceId: sel.objectId };
  }
  return { source: 'SUB_ACCOUNT', sourceId: sel.subAccountId };
}

function computeTradeAmount(
  sel: ExposureTradeSelection,
  direction: ExposureTradeDirection,
  buyAmountCentsPerAccount?: number,
): { type: ExposureTradeSide; amountCents: number } | null {
  if (direction === 'SELL') {
    return { type: 'SELL', amountCents: sel.valueCents };
  }
  if (direction === 'BUY') {
    const amountCents = buyAmountCentsPerAccount ?? 0;
    if (amountCents <= 0) return null;
    return { type: 'BUY', amountCents };
  }
  const diff = sel.expectedValueCents - sel.valueCents;
  if (Math.abs(diff) < MIN_REBALANCE_DIFF_CENTS) return null;
  return {
    type: diff > 0 ? 'BUY' : 'SELL',
    amountCents: Math.abs(diff),
  };
}

function capBuyAmountsByCash(
  trades: ActionableExposureTrade[],
): ActionableExposureTrade[] {
  const portfolioStats = new Map<
    string,
    { totalBuyCents: number; totalSellCents: number; cashCents: number }
  >();

  // All trades for the same portfolioId share cashAvailableForTradeCents; the
  // first trade encountered per portfolio supplies the cash value for scaling.
  for (const trade of trades) {
    const stats = portfolioStats.get(trade.portfolioId) ?? {
      totalBuyCents: 0,
      totalSellCents: 0,
      cashCents: trade.cashAvailableForTradeCents,
    };
    if (trade.type === 'BUY') stats.totalBuyCents += trade.moneyAllocatedCents;
    else stats.totalSellCents += trade.moneyAllocatedCents;
    portfolioStats.set(trade.portfolioId, stats);
  }

  const scaledTrades = trades.map((trade) => {
    if (trade.type !== 'BUY') return trade;
    const stats = portfolioStats.get(trade.portfolioId);
    if (!stats || stats.totalBuyCents === 0) return trade;
    const buyingPower = stats.cashCents + stats.totalSellCents;
    if (stats.totalBuyCents > buyingPower) {
      const scale = Math.max(0, buyingPower / stats.totalBuyCents);
      return {
        ...trade,
        moneyAllocatedCents: Math.round(trade.moneyAllocatedCents * scale),
      };
    }
    return trade;
  });

  return scaledTrades.filter((trade) => trade.moneyAllocatedCents > 0);
}

function groupTradesBySource(
  trades: ActionableExposureTrade[],
): ExposureTradeGroup[] {
  const tradesBySource = new Map<string, ExposureTradeGroup>();
  for (const trade of trades) {
    const key = `${trade.source}:${trade.sourceId}`;
    const group = tradesBySource.get(key) ?? {
      source: trade.source,
      sourceId: trade.sourceId,
      trades: [],
    };
    group.trades.push(trade);
    tradesBySource.set(key, group);
  }
  return Array.from(tradesBySource.values());
}

export function buildExposureSubTradeRequests(
  selections: ExposureTradeSelection[],
  direction: ExposureTradeDirection,
  buyAmountCentsPerAccount?: number,
): BuildExposureTradesResult {
  let skippedGoalCount = 0;
  const actionableTrades: ActionableExposureTrade[] = [];

  for (const sel of selections) {
    const sourceInfo = resolveSource(sel);
    if (!sourceInfo) {
      skippedGoalCount++;
      continue;
    }

    const tradeAmount = computeTradeAmount(
      sel,
      direction,
      buyAmountCentsPerAccount,
    );
    if (!tradeAmount) continue;

    actionableTrades.push({
      portfolioId: sel.portfolioId,
      source: sourceInfo.source,
      sourceId: sourceInfo.sourceId,
      subAccountId: sel.subAccountId,
      financialProductId: sel.financialProductId,
      portfolioReportId: sel.portfolioReportId,
      type: tradeAmount.type,
      moneyAllocatedCents: Math.round(tradeAmount.amountCents),
      currency: sel.currency,
      cashAvailableForTradeCents: sel.cashAvailableForTradeCents ?? 0,
    });
  }

  const cashCappedTrades = capBuyAmountsByCash(actionableTrades);
  if (cashCappedTrades.length === 0) {
    return { status: 'no_actionable', skippedGoalCount };
  }

  return {
    status: 'ready',
    trades: cashCappedTrades,
    groups: groupTradesBySource(cashCappedTrades),
    skippedGoalCount,
  };
}
